import { useEffect, useRef, useState } from 'react';
import { Bubble } from '../components/Bubble';
import { HuntBubble } from '../components/HuntBubble';
import { ApiException } from '../services/apiClient';
import { POOR_SIGNAL_MESSAGE } from '../services/messages';
import { type FindView, type ResolveResult, activeHunts, rarityLabel, rarityWeight } from '../models/types';
import { catalog } from '../state/catalog';
import { usePlayer } from '../state/player';
import { cooldownRemaining, isBusy, useScan } from '../state/scan';
import { useShell } from '../state/shell';
import { colors, rarityColors, withAlpha } from '../theme';
import { FindResultSheet } from './FindResultSheet';

/** How often hunt hints are re-measured while this screen is showing and auto-refresh is on. */
export const AUTO_REFRESH_MS = 45_000;

const nowSec = () => Math.floor(Date.now() / 1000);

/** Ticks every second: drives the scan cooldown and the hunts' time left, and hides a hunt as
 * soon as the device clock reaches its `expiresAt`. */
function useNow(): number {
  const [now, setNow] = useState(nowSec);
  useEffect(() => {
    const t = setInterval(() => setNow(nowSec()), 1000);
    return () => clearInterval(t);
  }, []);
  return now;
}

function countdown(until: number, now: number): string {
  const s = Math.min(86400, Math.max(0, until - now));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return m > 0 ? `${m}m ${String(r).padStart(2, '0')}s` : `${r}s`;
}

export function HuntScreen() {
  const now = useNow();
  const scan = useScan();
  const player = usePlayer((s) => s.player);
  const showToast = useShell((s) => s.showToast);
  const [open, setOpen] = useState<FindView | null>(null);
  const shown = useRef<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const refreshingRef = useRef(false);

  // Open the find sheet whenever a new find lands.
  useEffect(() => {
    if (scan.find && scan.find.findId !== shown.current) {
      shown.current = scan.find.findId;
      setOpen(scan.find);
    }
  }, [scan.find]);

  const celebrate = (result: ResolveResult | null, find: FindView) => {
    setOpen(null);
    shown.current = null;
    if (!result) return;
    const color = rarityColors[find.rarity];
    if (result.kind === 'picked_up') {
      showToast(
        result.item.assembled ? `🧩✨ Assembled! ${rarityLabel(find.rarity)} item complete!` : `${'✨'.repeat(rarityWeight(find.rarity))} Added to your collection!`,
        { color, durationMs: (2 + rarityWeight(find.rarity)) * 1000 },
      );
    } else if (result.kind === 'part_collected') {
      showToast(`🧩 Part ${result.hunt.collected.length}/${result.hunt.totalParts} collected. Keep hunting!`, { color, durationMs: 3000 });
    } else {
      showToast('Left behind. Maybe someone else will find it.', { durationMs: 3000 });
    }
  };

  /** Re-measure every hunt's hints from where the player is now. No scan, no cooldown. */
  const refreshLocations = async (silent = false) => {
    if (refreshingRef.current) return;
    refreshingRef.current = true;
    setRefreshing(true);
    try {
      await usePlayer.getState().refreshLocation();
    } catch (e) {
      if (!silent) {
        showToast(
          e instanceof ApiException && e.isAccuracyTooLow
            ? POOR_SIGNAL_MESSAGE
            : e instanceof ApiException
              ? e.message
              : 'Could not refresh locations. Check your connection and GPS.',
          { durationMs: 3000 },
        );
      }
    } finally {
      refreshingRef.current = false;
      setRefreshing(false);
    }
  };

  // Quietly re-measure the hints while the player can see them: a hunt is open, the page
  // is visible and the Hunt tab is selected (tabs stay mounted when hidden).
  const hasHunts = activeHunts(player?.hunts ?? [], now).length > 0;
  const refreshRef = useRef(refreshLocations);
  refreshRef.current = refreshLocations;
  useEffect(() => {
    if (!autoRefresh || !hasHunts) return;
    const t = setInterval(() => {
      if (document.visibilityState === 'visible' && useShell.getState().tab === 'hunt') void refreshRef.current(true);
    }, AUTO_REFRESH_MS);
    return () => clearInterval(t);
  }, [autoRefresh, hasHunts]);

  const cooldown = cooldownRemaining(scan.cooldownUntil, now);
  const canScan = !isBusy(scan) && cooldown === 0;
  const scanning = scan.phase === 'scanning';
  const hunts = activeHunts(player?.hunts ?? [], now);

  return (
    <section className="screen" aria-label="Hunt">
      <h1 className="app-bar">EthrHunt</h1>
      <div className="scroll">
        {player && <p className="t-title-l t-center" style={{ marginBottom: 16 }}>Hi {player.profile.displayName} 👋</p>}
        <div className="scan-wrap">
          <button type="button" className={`scan-btn${scanning ? ' scanning' : ''}`} disabled={!canScan} onClick={() => void scan.scan()} data-testid="scan-button" aria-label="Scan">
            {scanning && <span className="ring" />}
            <span className="inner">
              <span className="icon" aria-hidden="true">📡</span>
              <span className="label">{scanning ? 'Scanning…' : cooldown > 0 ? `${cooldown}s` : 'SCAN'}</span>
            </span>
          </button>
        </div>
        <div style={{ marginTop: 24 }}>
          <ResultArea now={now} />
        </div>
        {hunts.length > 0 && (
          <div style={{ marginTop: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <h2 className="t-title-l">Active hunts</h2>
              <label className="t-label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                Auto-refresh
                <input type="checkbox" role="switch" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} data-testid="auto-refresh" />
              </label>
            </div>
            <div className="stack" style={{ display: 'grid', gap: 12 }}>
              {hunts.map((h) => (
                <HuntBubble key={h.itemId} hunt={h} item={catalog.get(h.itemId)} now={now} onRefreshLocations={() => void refreshLocations()} refreshing={refreshing} />
              ))}
            </div>
          </div>
        )}
      </div>
      {open && <FindResultSheet find={open} item={catalog.get(open.itemId)} onDone={(r) => celebrate(r, open)} />}
    </section>
  );
}

function ResultArea({ now }: { now: number }) {
  const scan = useScan();
  switch (scan.phase) {
    case 'idle':
    case 'scanning':
    case 'resolving':
      if (scan.lastResolve == null && scan.phase === 'idle') {
        return (
          <Bubble color={withAlpha(colors.sky, 0.25)} style={{ textAlign: 'center' }}>
            Tap Scan to see what the Ethr has left nearby. Walk a block or two to search a new spot.
          </Bubble>
        );
      }
      return null;
    case 'found':
      return (
        <Bubble color={withAlpha(colors.lemon, 0.6)} style={{ textAlign: 'center' }}>
          <span className="t-title-l">Something is here! ✨</span>
        </Bubble>
      );
    case 'nothing': {
      const searched = scan.nothingReason === 'alreadySearched';
      return (
        <Bubble style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 44 }} aria-hidden="true">{searched ? '🕳️' : '🍃'}</div>
          <div className="t-title-l" style={{ margin: '8px 0 6px' }}>{searched ? 'You already searched here' : 'Nothing here right now'}</div>
          {scan.nextWindowAt != null && <p>This spot refreshes in {countdown(scan.nextWindowAt, now)}</p>}
          <p>…or walk somewhere new.</p>
        </Bubble>
      );
    }
    case 'error':
      return (
        <Bubble color="#f9dedc" style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 40 }} aria-hidden="true">😵‍💫</div>
          <p role="alert" style={{ marginTop: 8 }}>{scan.errorMessage ?? 'Something went wrong.'}</p>
        </Bubble>
      );
  }
}
